import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import Header from "@/components/Header";
import {
  ArrowLeft,
  Users,
  Eye,
  Sparkles,
  CheckCircle2,
  ArrowRight,
  Waves,
  Brain,
  CloudSun,
  Radar,
  Compass,
  BookOpen,
  MessageCircle,
  MapPin,
  ShieldCheck,
  Globe,
  Quote,
  X,
  Check,
  CreditCard,
  Star,
  FileText,
  DollarSign,
  Bitcoin,
  Camera,
  Lock,
  BarChart3,
  TrendingDown,
  Radio,
  UserCheck,
  WifiOff,
  Phone,
  CalendarDays,
  Shield,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
  HelpCircle,
  Zap,
  Clock,
  Monitor,
  Smartphone,
  Chrome,
  Heart,
  ExternalLink,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const POINT_ICONS = [
  Waves, Brain, CloudSun, Radar, Compass,
  BookOpen, MessageCircle, MapPin, ShieldCheck, Globe,
];

const LEDGERA_ICONS = [
  FileText, DollarSign, TrendingDown, Bitcoin, Camera, Lock, BarChart3, BarChart3,
];

const GRIDDOWN_ICONS = [
  Radio, UserCheck, WifiOff, Phone, CalendarDays, Shield,
];

const FOCUSLOCK_ICONS = [
  Lock, Brain, Users, TrendingDown, Zap, CalendarDays,
  UserCheck, Compass, BarChart3, Sparkles, Eye, BookOpen,
  Star, ShieldCheck,
];

const SAFECLASS_ICONS = [
  ShieldCheck, Globe, MessageCircle, Clock, Monitor, Smartphone,
  Chrome, CheckCircle2,
];

const MOI_ICONS = [
  Users, BookOpen, Brain, AlertTriangle, Eye, Globe,
  Heart, UserCheck, Globe, MessageCircle,
];

const GRIDNET_ICONS = [
  Smartphone, Radio, Compass, AlertTriangle, Globe, Users,
];

const AFTERCARE_ICONS = [
  Lock, ShieldCheck, UserCheck, Heart, Users, MapPin,
  FileText, BookOpen, CalendarDays, Phone, CloudSun,
];

const FORGE_ICONS = [
  Compass, Zap, Sparkles, Globe, BarChart3, MessageCircle, MapPin,
];

const FAN_ICONS = [
  ShieldCheck, Brain, Users, MapPin, BarChart3, AlertTriangle,
];

export default function AppDetailPage() {
  const { slug } = useParams();
  const [app, setApp] = useState(null);
  const [allApps, setAllApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    setOpenFaq(null);
    Promise.all([
      axios.get(`${API}/apps/${slug}`),
      axios.get(`${API}/apps`),
    ])
      .then(([detail, list]) => {
        setApp(detail.data);
        setAllApps(list.data);
      })
      .catch((err) => console.error(err))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 text-white">
        <Header />
        <div className="mx-auto max-w-4xl px-6 py-20">
          <div className="animate-pulse space-y-6">
            <div className="h-4 w-24 rounded bg-white/[0.06]" />
            <div className="h-10 w-80 rounded bg-white/[0.06]" />
            <div className="h-4 w-full rounded bg-white/[0.04]" />
            <div className="h-4 w-2/3 rounded bg-white/[0.04]" />
          </div>
        </div>
      </div>
    );
  }

  if (!app || app.error) {
    return (
      <div className="min-h-screen bg-slate-950 text-white">
        <Header />
        <div className="mx-auto max-w-4xl px-6 py-20 text-center">
          <h1 className="text-2xl font-bold mb-4">App not found</h1>
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-cyan-300 hover:text-cyan-200 transition-colors"
          >
            <ArrowLeft size={16} />
            Back to home
          </Link>
        </div>
      </div>
    );
  }

  const hasMainPoints = app.main_points && app.main_points.length > 0;
  const hasProComparison = app.pro_comparison && app.pro_comparison.length > 0;
  const hasPricing = app.pricing && app.pricing.length > 0;
  const hasTestimonials = app.testimonials && app.testimonials.length > 0;
  const hasCompetitorComparison = app.competitor_comparison && app.competitor_comparison.rows?.length > 0;
  const hasFaq = app.faq && app.faq.length > 0;
  const hasScenarios = app.scenarios && app.scenarios.length > 0;
  const hasStats = app.stats && app.stats.length > 0;
  const hasHowItWorks = app.how_it_works && app.how_it_works.length > 0;
  const hasWhoNeeds = app.who_needs && app.who_needs.length > 0;

  let iconSet = POINT_ICONS;
  if (app.slug === "ledgera-invoice-pro") iconSet = LEDGERA_ICONS;
  if (app.slug === "grid-down") iconSet = GRIDDOWN_ICONS;
  if (app.slug === "focus-lock") iconSet = FOCUSLOCK_ICONS;
  if (app.slug === "safeclass-shield") iconSet = SAFECLASS_ICONS;
  if (app.slug === "men-of-integrity") iconSet = MOI_ICONS;
  if (app.slug === "grid-down-network") iconSet = GRIDNET_ICONS;
  if (app.slug === "aftercare") iconSet = AFTERCARE_ICONS;
  if (app.slug === "forge-engine-nexus") iconSet = FORGE_ICONS;
  if (app.slug === "fan-firstaid-network") iconSet = FAN_ICONS;

  const currentIndex = allApps.findIndex((a) => a.slug === slug);
  const nextApp = allApps[(currentIndex + 1) % allApps.length];
  const prevApp = allApps[(currentIndex - 1 + allApps.length) % allApps.length];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 text-white">
      <Header />

      {/* Breadcrumb */}
      <div className="mx-auto max-w-6xl px-8 pt-10">
        <Link
          to="/"
          data-testid="back-to-home"
          className="inline-flex items-center gap-2 text-sm font-medium text-slate-400 hover:text-white transition-colors"
        >
          <ArrowLeft size={16} />
          Back to Portfolio
        </Link>
      </div>

      {/* App Header */}
      <section data-testid="app-detail-header" className="mx-auto max-w-6xl px-8 pt-10 pb-16">
        <div>
          {/* Logo */}
          {app.logo && (
            <div className="mb-8">
              <img
                src={app.logo}
                alt={`${app.name} logo`}
                className="w-36 h-36 md:w-44 md:h-44 object-contain rounded-2xl border border-slate-800 bg-slate-900/50 p-4"
              />
            </div>
          )}
          <div className="flex flex-wrap items-center gap-3 mb-6">
            <span className="rounded-xl border border-blue-400/20 bg-blue-950/50 px-4 py-2 text-xs font-semibold uppercase tracking-wider text-blue-200">
              {app.category}
            </span>
            <span
              className={`rounded-xl px-4 py-2 text-xs font-semibold uppercase tracking-wider ${
                app.status === "Featured"
                  ? "border border-blue-400/30 bg-blue-950/50 text-blue-300"
                  : "border border-slate-700 bg-slate-800/50 text-slate-400"
              }`}
            >
              {app.status}
            </span>
          </div>
          <h1
            data-testid="app-detail-name"
            className="text-5xl font-bold tracking-tight md:text-6xl lg:text-7xl"
          >
            {app.name}
          </h1>
          {app.tagline && (
            <p className="mt-5 text-xl text-blue-200/70 italic font-medium">
              "{app.tagline}"
            </p>
          )}
          <p className="mt-8 max-w-4xl text-lg leading-relaxed text-slate-300">
            {app.description}
          </p>
          
          {/* Privacy Policy Link - Prominent */}
          {app.privacyPolicyUrl && (
            <div className="mt-8">
              <Link
                to={app.privacyPolicyUrl}
                className="inline-flex items-center gap-2.5 px-6 py-3 rounded-xl border border-blue-400/30 bg-blue-950/50 text-blue-400 hover:bg-blue-950/70 hover:border-blue-400/50 transition-all group"
              >
                <Shield className="w-5 h-5" />
                <span className="font-semibold">View Privacy Policy</span>
                <ExternalLink className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* Highlights */}
      <section className="mx-auto max-w-6xl px-8 pb-16">
        <div className="flex flex-wrap gap-3">
          {app.highlights.map((h) => (
            <span
              key={h}
              data-testid={`highlight-${h.toLowerCase().replace(/\s+/g, "-")}`}
              className="rounded-xl border border-slate-700 bg-slate-800/50 px-5 py-2.5 text-sm font-medium text-slate-200"
            >
              {h}
            </span>
          ))}
        </div>
      </section>

      {/* Stats Bar */}
      {hasStats && (
        <section data-testid="stats-section" className="mx-auto max-w-6xl px-8 pb-20">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
            {app.stats.map((stat, i) => (
              <div
                key={i}
                data-testid={`stat-${i}`}
                className="rounded-2xl border border-slate-800 bg-gradient-to-br from-slate-900/80 to-slate-900/40 p-6 text-center backdrop-blur-sm"
              >
                <p className="text-3xl md:text-4xl font-bold text-blue-400">{stat.value}</p>
                <p className="mt-2 text-xs text-slate-400 uppercase tracking-wider font-semibold">{stat.label}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Main Points (rich content) */}
      {hasMainPoints && (
        <section data-testid="app-main-points" className="mx-auto max-w-6xl px-8 pb-24">
          <div className="mb-10">
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-blue-400 mb-2">
              Key Capabilities
            </p>
            <h2 className="text-3xl font-bold tracking-tight md:text-4xl">
              Everything you need, unified
            </h2>
          </div>

          <div className="grid gap-6 md:grid-cols-2">
            {app.main_points.map((point, i) => {
              const Icon = iconSet[i % iconSet.length];
              return (
                <div
                  key={i}
                  data-testid={`main-point-${i}`}
                  className="group rounded-2xl border border-slate-800 bg-gradient-to-br from-slate-900/80 to-slate-900/40 p-7 backdrop-blur-sm transition-all duration-300 hover:border-blue-500/30 hover:shadow-lg hover:shadow-blue-900/10"
                >
                  <div className="flex items-start gap-4">
                    <div className="shrink-0 inline-flex rounded-xl border border-slate-700 bg-slate-800/50 p-4 transition-all duration-300 group-hover:border-blue-500/30 group-hover:bg-blue-950/30">
                      <Icon size={22} className="text-slate-400 transition-colors group-hover:text-blue-400" />
                    </div>
                    <div>
                      <div className="flex items-center gap-3 mb-3">
                        <span className="text-xs font-mono text-blue-400/50 font-semibold">
                          {String(i + 1).padStart(2, "0")}
                        </span>
                        <h3 className="text-lg font-bold text-white">
                          {point.title}
                        </h3>
                      </div>
                      <p className="text-sm leading-relaxed text-slate-300">
                        {point.description}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* How It Works */}
      {hasHowItWorks && (
        <section data-testid="how-it-works-section" className="border-y border-white/[0.06] bg-white/[0.015]">
          <div className="mx-auto max-w-5xl px-6 py-20">
            <div className="mb-10">
              <p className="text-xs font-medium uppercase tracking-[0.25em] text-cyan-300">
                How It Works
              </p>
              <h2 className="mt-3 text-2xl font-bold tracking-tight md:text-3xl">
                Get started in minutes
              </h2>
            </div>
            <div className="grid gap-6 md:grid-cols-4">
              {app.how_it_works.map((step, i) => (
                <div key={i} data-testid={`how-step-${i}`} className="relative">
                  <div className="mb-4 inline-flex h-10 w-10 items-center justify-center rounded-full border border-cyan-400/30 bg-cyan-400/10 text-sm font-bold text-cyan-300">
                    {step.step}
                  </div>
                  <h3 className="text-base font-semibold text-white mb-2">{step.title}</h3>
                  <p className="text-sm leading-6 text-slate-400">{step.description}</p>
                  {i < app.how_it_works.length - 1 && (
                    <div className="hidden md:block absolute top-5 left-full w-full">
                      <div className="h-[1px] w-3/4 bg-gradient-to-r from-cyan-400/20 to-transparent" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Who Needs This */}
      {hasWhoNeeds && (
        <section data-testid="who-needs-section" className="mx-auto max-w-5xl px-6 py-20">
          <div className="mb-8">
            <p className="text-xs font-medium uppercase tracking-[0.25em] text-cyan-300">
              Who Needs {app.name}?
            </p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {app.who_needs.map((item, i) => (
              <div
                key={i}
                data-testid={`who-needs-${i}`}
                className="rounded-2xl border border-white/[0.08] bg-white/[0.03] p-5 transition-all hover:border-cyan-400/15"
              >
                <div className="mb-2 inline-flex rounded-lg bg-cyan-400/[0.06] p-2">
                  <CheckCircle2 size={16} className="text-cyan-400" />
                </div>
                <h3 className="text-sm font-semibold text-white">{item.audience}</h3>
                <p className="mt-1 text-xs text-slate-500">{item.description}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Tagline Options */}
      {app.tagline_options && app.tagline_options.length > 0 && (
        <section className="mx-auto max-w-5xl px-6 pb-16">
          <div className="rounded-2xl border border-white/[0.06] bg-gradient-to-br from-cyan-950/20 to-transparent p-8">
            <div className="flex items-center gap-3 mb-6">
              <Quote size={18} className="text-cyan-300/60" />
              <p className="text-xs font-medium uppercase tracking-[0.25em] text-cyan-300">
                Taglines
              </p>
            </div>
            <div className="space-y-4">
              {app.tagline_options.map((tl, i) => (
                <p key={i} className="text-lg font-medium text-white/80 italic pl-6 border-l-2 border-cyan-400/20">
                  "{tl}"
                </p>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Pro Comparison Table (Ledgera-style Free vs Pro) */}
      {hasProComparison && (
        <section data-testid="pro-comparison-section" className="mx-auto max-w-5xl px-6 pb-16">
          <div className="mb-8">
            <p className="text-xs font-medium uppercase tracking-[0.25em] text-cyan-300">
              {app.name} Pro
            </p>
            <h2 className="mt-3 text-2xl font-bold tracking-tight md:text-3xl">
              Unlock Your Full Financial Potential
            </h2>
          </div>
          <div className="rounded-2xl border border-white/[0.08] bg-white/[0.03] overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/[0.08]">
                  <th className="text-left px-6 py-4 text-sm font-medium text-slate-400">Feature</th>
                  <th className="text-center px-4 py-4 text-sm font-medium text-slate-400 w-24">Free</th>
                  <th className="text-center px-4 py-4 text-sm font-medium text-cyan-300 w-24">Pro</th>
                </tr>
              </thead>
              <tbody>
                {app.pro_comparison.map((row, i) => (
                  <tr key={i} className={`border-b border-white/[0.04] ${i % 2 === 0 ? "bg-white/[0.01]" : ""}`}>
                    <td className="px-6 py-3.5 text-sm text-slate-300">{row.feature}</td>
                    <td className="text-center px-4 py-3.5">
                      {row.free
                        ? <Check size={16} className="mx-auto text-emerald-400" />
                        : <X size={16} className="mx-auto text-slate-600" />}
                    </td>
                    <td className="text-center px-4 py-3.5">
                      {row.pro
                        ? <Check size={16} className="mx-auto text-cyan-400" />
                        : <X size={16} className="mx-auto text-slate-600" />}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Competitor Comparison Table (Grid Down style - multiple competitors) */}
      {hasCompetitorComparison && (
        <section data-testid="competitor-comparison-section" className="mx-auto max-w-5xl px-6 pb-16">
          <div className="mb-8">
            <p className="text-xs font-medium uppercase tracking-[0.25em] text-cyan-300">
              Comparison
            </p>
            <h2 className="mt-3 text-2xl font-bold tracking-tight md:text-3xl">
              What Makes {app.name} Different
            </h2>
          </div>
          <div className="rounded-2xl border border-white/[0.08] bg-white/[0.03] overflow-x-auto">
            <table className="w-full min-w-[600px]">
              <thead>
                <tr className="border-b border-white/[0.08]">
                  <th className="text-left px-6 py-4 text-sm font-medium text-slate-400">Feature</th>
                  {app.competitor_comparison.competitors.map((comp, i) => (
                    <th
                      key={i}
                      className={`text-center px-4 py-4 text-sm font-medium w-32 ${
                        i === 0 ? "text-cyan-300" : "text-slate-400"
                      }`}
                    >
                      {comp}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {app.competitor_comparison.rows.map((row, i) => (
                  <tr key={i} className={`border-b border-white/[0.04] ${i % 2 === 0 ? "bg-white/[0.01]" : ""}`}>
                    <td className="px-6 py-3.5 text-sm text-slate-300 font-medium">{row.feature}</td>
                    {row.values.map((val, j) => {
                      const isFirst = j === 0;
                      const isPositive = val === "Yes" || val === "Real-time" || val === "5-min breadcrumbs" || val === "No servers" || val === "Hardware pairing" || val === "Circles only";
                      const isNegative = val === "No";
                      return (
                        <td key={j} className={`text-center px-4 py-3.5 text-sm ${isFirst ? "font-medium" : ""}`}>
                          {isNegative ? (
                            <X size={16} className="mx-auto text-slate-600" />
                          ) : (
                            <span className={isFirst && isPositive ? "text-cyan-300" : isPositive ? "text-emerald-400" : "text-slate-400"}>
                              {val}
                            </span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Real Scenarios */}
      {hasScenarios && (
        <section data-testid="scenarios-section" className="border-y border-white/[0.06] bg-white/[0.015]">
          <div className="mx-auto max-w-5xl px-6 py-20">
            <div className="mb-10">
              <p className="text-xs font-medium uppercase tracking-[0.25em] text-cyan-300">
                Real Scenarios
              </p>
              <h2 className="mt-3 text-2xl font-bold tracking-tight md:text-3xl">
                Where {app.name} Saves Lives
              </h2>
            </div>
            <div className="grid gap-6 md:grid-cols-2">
              {app.scenarios.map((scenario, i) => (
                <div
                  key={i}
                  data-testid={`scenario-${i}`}
                  className="rounded-2xl border border-white/[0.08] bg-slate-900/50 p-6"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className="inline-flex rounded-lg bg-amber-400/[0.08] border border-amber-400/20 p-2">
                      <AlertTriangle size={16} className="text-amber-400" />
                    </div>
                    <h3 className="text-base font-semibold text-white">{scenario.title}</h3>
                  </div>
                  <p className="text-sm leading-7 text-slate-400">{scenario.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Pricing */}
      {hasPricing && (
        <section data-testid="pricing-section" className="mx-auto max-w-5xl px-6 py-20">
          <div className="mb-8">
            <p className="text-xs font-medium uppercase tracking-[0.25em] text-cyan-300">
              Pricing
            </p>
            <h2 className="mt-3 text-2xl font-bold tracking-tight">
              Choose your plan
            </h2>
          </div>
          <div className={`grid gap-5 ${app.pricing.length === 2 ? "sm:grid-cols-2 max-w-3xl" : app.pricing.length === 4 ? "sm:grid-cols-2 lg:grid-cols-4" : "sm:grid-cols-3"}`}>
            {app.pricing.map((plan, i) => {
              const isBest = plan.note && plan.note.includes("Best Value");
              const isPremium = plan.plan === "Premium" || plan.price !== "Free";
              return (
                <div
                  key={i}
                  data-testid={`pricing-plan-${i}`}
                  className={`relative rounded-2xl border p-6 transition-all ${
                    isBest || (app.pricing.length === 2 && isPremium)
                      ? "border-cyan-400/30 bg-gradient-to-br from-cyan-950/30 to-transparent shadow-lg shadow-cyan-950/20"
                      : "border-white/[0.08] bg-white/[0.03]"
                  }`}
                >
                  {isBest && (
                    <span className="absolute -top-3 left-6 rounded-full bg-cyan-400 px-3 py-0.5 text-[11px] font-semibold text-slate-950">
                      Best Value
                    </span>
                  )}
                  <div className="flex items-center gap-2 mb-3">
                    <CreditCard size={16} className={isPremium ? "text-cyan-300" : "text-slate-500"} />
                    <h3 className="text-base font-semibold text-white">{plan.plan}</h3>
                  </div>
                  <p className="text-2xl font-bold text-white">{plan.price}</p>
                  {plan.note && (
                    <p className="mt-1 text-sm text-slate-400">{plan.note}</p>
                  )}
                  {plan.details && plan.details.length > 0 && (
                    <ul className="mt-4 space-y-2">
                      {plan.details.map((d, j) => (
                        <li key={j} className="flex items-center gap-2 text-sm text-slate-400">
                          <CheckCircle2 size={14} className="shrink-0 text-cyan-400/60" />
                          {d}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Testimonials */}
      {hasTestimonials && (
        <section data-testid="testimonials-section" className="mx-auto max-w-5xl px-6 pb-20">
          <div className="mb-8">
            <p className="text-xs font-medium uppercase tracking-[0.25em] text-cyan-300">
              What People Say
            </p>
            <h2 className="mt-3 text-2xl font-bold tracking-tight">
              Why {app.name}?
            </h2>
          </div>
          <div className="grid gap-5 md:grid-cols-3">
            {app.testimonials.map((t, i) => (
              <div
                key={i}
                data-testid={`testimonial-${i}`}
                className="rounded-2xl border border-white/[0.08] bg-white/[0.03] p-6"
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, s) => (
                    <Star key={s} size={14} className="text-amber-400 fill-amber-400" />
                  ))}
                </div>
                <p className="text-sm leading-7 text-slate-300 italic mb-5">
                  "{t.quote}"
                </p>
                <div className="border-t border-white/[0.06] pt-4">
                  <p className="text-sm font-semibold text-white">{t.name}</p>
                  <p className="text-xs text-slate-500">{t.title}</p>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* FAQ */}
      {hasFaq && (
        <section data-testid="faq-section" className="border-t border-white/[0.06] bg-white/[0.015]">
          <div className="mx-auto max-w-5xl px-6 py-20">
            <div className="mb-10">
              <p className="text-xs font-medium uppercase tracking-[0.25em] text-cyan-300">
                FAQ
              </p>
              <h2 className="mt-3 text-2xl font-bold tracking-tight md:text-3xl">
                Frequently Asked Questions
              </h2>
            </div>
            <div className="space-y-3 max-w-3xl">
              {app.faq.map((item, i) => (
                <div
                  key={i}
                  data-testid={`faq-${i}`}
                  className="rounded-2xl border border-white/[0.08] bg-white/[0.03] overflow-hidden"
                >
                  <button
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    className="w-full flex items-center justify-between px-6 py-4 text-left transition-colors hover:bg-white/[0.02]"
                  >
                    <div className="flex items-center gap-3">
                      <HelpCircle size={16} className="shrink-0 text-cyan-400/60" />
                      <span className="text-sm font-medium text-white">{item.question}</span>
                    </div>
                    {openFaq === i ? (
                      <ChevronUp size={16} className="shrink-0 text-slate-500" />
                    ) : (
                      <ChevronDown size={16} className="shrink-0 text-slate-500" />
                    )}
                  </button>
                  {openFaq === i && (
                    <div className="px-6 pb-4 pl-12">
                      <p className="text-sm leading-7 text-slate-400">{item.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Features, Audience, Vision — standard layout (no main_points) */}
      {!hasMainPoints && (
        <section className="mx-auto max-w-5xl px-6 pb-20">
          <div className="grid gap-8 md:grid-cols-2">
            <div
              data-testid="app-features"
              className="rounded-2xl border border-white/[0.08] bg-white/[0.03] p-7"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="inline-flex rounded-xl border border-white/[0.08] bg-white/[0.04] p-2.5">
                  <Sparkles size={18} className="text-cyan-300" />
                </div>
                <h2 className="text-xl font-semibold">Features</h2>
              </div>
              <ul className="space-y-4">
                {app.features?.map((feat, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckCircle2 size={16} className="mt-0.5 shrink-0 text-cyan-400/60" />
                    <span className="text-sm leading-6 text-slate-300">{feat}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="space-y-8">
              <div
                data-testid="app-audience"
                className="rounded-2xl border border-white/[0.08] bg-white/[0.03] p-7"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="inline-flex rounded-xl border border-white/[0.08] bg-white/[0.04] p-2.5">
                    <Users size={18} className="text-cyan-300" />
                  </div>
                  <h2 className="text-xl font-semibold">Who It's For</h2>
                </div>
                <p className="text-sm leading-7 text-slate-400">{app.audience}</p>
              </div>

              <div
                data-testid="app-vision"
                className="rounded-2xl border border-white/[0.08] bg-white/[0.03] p-7"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="inline-flex rounded-xl border border-white/[0.08] bg-white/[0.04] p-2.5">
                    <Eye size={18} className="text-cyan-300" />
                  </div>
                  <h2 className="text-xl font-semibold">Vision</h2>
                </div>
                <p className="text-sm leading-7 text-slate-400">{app.vision}</p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Audience + Vision for apps WITH main_points */}
      {hasMainPoints && (
        <section className="mx-auto max-w-5xl px-6 pb-20">
          <div className="grid gap-8 md:grid-cols-2">
            <div
              data-testid="app-audience"
              className="rounded-2xl border border-white/[0.08] bg-white/[0.03] p-7"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="inline-flex rounded-xl border border-white/[0.08] bg-white/[0.04] p-2.5">
                  <Users size={18} className="text-cyan-300" />
                </div>
                <h2 className="text-xl font-semibold">Who It's For</h2>
              </div>
              <p className="text-sm leading-7 text-slate-400">{app.audience}</p>
            </div>

            <div
              data-testid="app-vision"
              className="rounded-2xl border border-white/[0.08] bg-white/[0.03] p-7"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="inline-flex rounded-xl border border-white/[0.08] bg-white/[0.04] p-2.5">
                  <Eye size={18} className="text-cyan-300" />
                </div>
                <h2 className="text-xl font-semibold">Vision</h2>
              </div>
              <p className="text-sm leading-7 text-slate-400">{app.vision}</p>
            </div>
          </div>
        </section>
      )}

      {/* Navigation between apps */}
      <section className="border-t border-white/[0.06] bg-white/[0.015]">
        <div className="mx-auto max-w-5xl px-6 py-10">
          <div className="flex items-center justify-between">
            <Link
              to={`/apps/${prevApp?.slug}`}
              data-testid="prev-app-link"
              className="group flex items-center gap-3 text-sm text-slate-500 hover:text-white transition-colors"
            >
              <ArrowLeft size={16} className="transition-transform group-hover:-translate-x-1" />
              <div>
                <div className="text-xs text-slate-600">Previous</div>
                <div className="font-medium">{prevApp?.name}</div>
              </div>
            </Link>
            <Link
              to={`/apps/${nextApp?.slug}`}
              data-testid="next-app-link"
              className="group flex items-center gap-3 text-sm text-slate-500 hover:text-white transition-colors text-right"
            >
              <div>
                <div className="text-xs text-slate-600">Next</div>
                <div className="font-medium">{nextApp?.name}</div>
              </div>
              <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </div>
      </section>

      {/* Image Gallery */}
      {app.gallery && app.gallery.length > 0 && (
        <section className="mx-auto max-w-7xl px-6 py-16 bg-gradient-to-b from-slate-900/50 to-slate-950">
          <h2 className="text-3xl font-bold tracking-tight mb-8 text-center">
            See It In Action
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {app.gallery.map((imageUrl, index) => (
              <div
                key={index}
                className="group relative overflow-hidden rounded-xl border border-white/10 hover:border-cyan-400/30 transition-all duration-300"
              >
                <img
                  src={imageUrl}
                  alt={`${app.name} feature ${index + 1}`}
                  className="w-full h-auto object-cover transition-transform duration-300 group-hover:scale-105"
                />
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Privacy Policy Section */}
      {app.privacyPolicyUrl && (
        <section className="mx-auto max-w-7xl px-6 py-12 border-t border-white/[0.06]">
          <div className="flex items-center justify-center gap-3">
            <Shield className="w-5 h-5 text-cyan-400" />
            <a
              href={app.privacyPolicyUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-base font-medium text-cyan-400 hover:text-cyan-300 transition-colors group"
            >
              View Privacy Policy
              <ExternalLink className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </a>
          </div>
        </section>
      )}

      {/* Terms of Service Section */}
      {app.termsOfServiceUrl && (
        <section className="mx-auto max-w-7xl px-6 py-8 border-t border-white/[0.06]">
          <div className="flex items-center justify-center gap-3">
            <Shield className="w-5 h-5 text-blue-400" />
            <a
              href={app.termsOfServiceUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-base font-medium text-blue-400 hover:text-blue-300 transition-colors group"
            >
              View Terms of Service
              <ExternalLink className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </a>
          </div>
        </section>
      )}

      {/* AI Disclosure Section */}
      {app.disclosureUrl && (
        <section className="mx-auto max-w-7xl px-6 py-8 border-t border-white/[0.06]">
          <div className="flex items-center justify-center gap-3">
            <Shield className="w-5 h-5 text-amber-400" />
            <a
              href={app.disclosureUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-base font-medium text-amber-400 hover:text-amber-300 transition-colors group"
            >
              View AI Disclosures
              <ExternalLink className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </a>
          </div>
        </section>
      )}

      {/* Marketing Materials Section - Only for LifeCorp AI */}
      {app.slug === 'lifecorp-ai' && (
        <section className="mx-auto max-w-7xl px-6 py-8 border-t border-white/[0.06]">
          <div className="flex items-center justify-center gap-3">
            <Shield className="w-5 h-5 text-purple-400" />
            <Link
              to={`/marketing/${app.slug}`}
              className="inline-flex items-center gap-2 text-base font-medium text-purple-400 hover:text-purple-300 transition-colors group"
            >
              View Marketing Materials
              <ExternalLink className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </Link>
          </div>
        </section>
      )}

      {/* Delete Account Link Section */}
      <section className="mx-auto max-w-7xl px-6 py-8 border-t border-white/[0.06]">
        <div className="text-center">
          <Link
            to={`/${app.slug}/delete-account`}
            className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-red-400 transition-colors"
          >
            Delete Your Account
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-800 bg-slate-900/50 backdrop-blur-sm">
        <div className="mx-auto max-w-7xl px-8 py-12 flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
          <div>
            <div className="text-lg font-bold text-white">
              Perfect Peace Enterprise
            </div>
            <p className="mt-1 text-xs text-slate-500">
              Building digital products for real-world impact
            </p>
          </div>
          <Link
            to="/"
            className="text-sm font-medium text-slate-400 hover:text-white transition-colors"
          >
            Back to Portfolio
          </Link>
        </div>
      </footer>
    </div>
  );
}
